import { useEffect, useRef } from "react";
import { onPhaseChange, scrollState, hasSeenIntro } from "../state/scroll";

/**
 * Brief crossfade overlay that hides the moment of phase change into PLOT.
 * Without this, the swap from the 2D map to the 3D/DOM content below is an
 * instant cut — the user sees a hard frame change that reads as cheap.
 *
 * Painted via direct DOM ref mutation, NOT React state. The phase change
 * also triggers an immediate, synchronous `window.scrollTo(0,0)` (see
 * SmoothScroll.jsx) to reset the page. If the veil only reached opacity 1
 * via a CSS transition (state -> re-render -> fade in over ~280ms), that
 * scroll reset would be visible for a frame or two *underneath* the
 * still-transparent veil. Painting it instantly, synchronously, before the
 * scroll reset runs, guarantees the cover is already opaque by the time
 * anything moves.
 */
let veilEl = null;
let hideTimer = null;

export function showVeilInstant() {
  if (!veilEl) return;
  window.clearTimeout(hideTimer);
  veilEl.style.transition = "none";
  veilEl.style.opacity = "1";
  void veilEl.offsetHeight; // force-flush before re-enabling transitions
}

export function scheduleVeilFadeOut(delay = 420) {
  if (!veilEl) return;
  window.clearTimeout(hideTimer);
  hideTimer = window.setTimeout(() => {
    if (!veilEl) return;
    veilEl.style.transition = "opacity 520ms cubic-bezier(0.4, 0, 0.2, 1)";
    veilEl.style.opacity = "0";
  }, delay);
}

export default function PhaseTransitionOverlay() {
  const ref = useRef(null);

  useEffect(() => {
    veilEl = ref.current;
    return () => { veilEl = null; };
  }, []);

  useEffect(() => {
    let lastPhase = scrollState.phase;

    const off = onPhaseChange((cur) => {
      if (cur === lastPhase) return;
      // Skip the transition veil entirely on return SPA visits — there's
      // no cinematic to mask, the user is already in PLOT.
      if (hasSeenIntro() && (lastPhase === null || lastPhase === "INTRO")) {
        lastPhase = cur;
        return;
      }
      const involvesPlot = cur === "PLOT" || lastPhase === "PLOT";
      if (involvesPlot) {
        // Fallback safety net — SmoothScroll normally already calls this
        // synchronously before the scroll reset. Re-affirming here is a
        // harmless no-op in that case.
        showVeilInstant();
        scheduleVeilFadeOut();
      }
      lastPhase = cur;
    });
    return () => off();
  }, []);

  return (
    <div
      ref={ref}
      aria-hidden="true"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9000,
        background:
          "radial-gradient(ellipse at center, #060912 0%, #02030a 70%, #000000 100%)",
        opacity: 0,
        // Never block input — the overlay is purely visual.
        pointerEvents: "none",
      }}
    />
  );
}
